// Layer-1 trigger questions for the report builder (decision 2026-06-04 in
// docs/ramdocs-user-input-model-from-rams-app.md): a short set of job-specific
// questions whose answers change document content — hazard modules in/out,
// permits added/removed, competence/substances/emergency/sequence content,
// job-specific scope notes, readiness checks. Follow-up (Layer-2) questions are
// revealed by their parent answer. The full hazard list stays available behind
// "Advanced".
//
// This file is the ENGINE; the question sets live in lib/triggers/ — one module
// per report batch, keyed by report slug, merged into REPORT_TRIGGERS below.

import { splitEmergency, type RamsReport } from "./reports";
import { NOT_SURE, type TriggerQuestion } from "./triggers/types";
import { LAUNCH_TRIGGERS } from "./triggers/launch";
import { WAH_ACCESS_TRIGGERS } from "./triggers/workAtHeightAccess";
import { WAH_ROOFS_TRIGGERS } from "./triggers/workAtHeightRoofs";
import { ELECTRICAL_TRIGGERS } from "./triggers/electrical";
import { GROUNDWORKS_TRIGGERS } from "./triggers/groundworks";
import { LIFTING_OPS_TRIGGERS } from "./triggers/liftingOps";
import { SITE_PLANT_TRIGGERS } from "./triggers/sitePlant";
import { HOT_WORKS_TRIGGERS } from "./triggers/hotWorks";
import { COSHH_DUSTS_TRIGGERS } from "./triggers/coshhDusts";
import { COSHH_CHEMICALS_TRIGGERS } from "./triggers/coshhChemicals";
import { DEMOLITION_TRIGGERS } from "./triggers/demolition";
import { TRADES_STRUCTURAL_TRIGGERS } from "./triggers/tradesStructural";
import { TRADES_INTERNAL_TRIGGERS } from "./triggers/tradesInternal";
import { SITE_GENERAL_TRIGGERS } from "./triggers/siteGeneral";
import { GENERATED_ACTIVE_SLUGS } from "./reportLinks";
import GENERATED_TRIGGERS from "../content/reports/generated-triggers.json";

export type { TriggerQuestion, TriggerEffect } from "./triggers/types";
export { NOT_SURE } from "./triggers/types";

// Generated trigger sets (content-engine `export:reports`) for the templates the
// hand-authored sets don't cover. Spread FIRST so any hand-authored set wins.
const GENERATED: Record<string, TriggerQuestion[]> = Object.fromEntries(
  Object.entries(GENERATED_TRIGGERS as unknown as Record<string, TriggerQuestion[]>).filter(([slug]) => GENERATED_ACTIVE_SLUGS.has(slug)),
);

export const REPORT_TRIGGERS: Record<string, TriggerQuestion[]> = {
  ...GENERATED,
  ...LAUNCH_TRIGGERS,
  ...WAH_ACCESS_TRIGGERS,
  ...WAH_ROOFS_TRIGGERS,
  ...ELECTRICAL_TRIGGERS,
  ...GROUNDWORKS_TRIGGERS,
  ...LIFTING_OPS_TRIGGERS,
  ...SITE_PLANT_TRIGGERS,
  ...HOT_WORKS_TRIGGERS,
  ...COSHH_DUSTS_TRIGGERS,
  ...COSHH_CHEMICALS_TRIGGERS,
  ...DEMOLITION_TRIGGERS,
  ...TRADES_STRUCTURAL_TRIGGERS,
  ...TRADES_INTERNAL_TRIGGERS,
  ...SITE_GENERAL_TRIGGERS,
};

export type TriggerOutcome = {
  /** Hazards forced off / on by current answers. */
  off: Set<string>;
  on: Set<string>;
  permitsAdd: string[];
  permitsRemove: string[];
  scopeLines: string[];
  /** Readiness checks derived from answers (failCheck / requiredCheck). */
  checks: { label: string; ok: boolean }[];
  /** Document-content effects (competence / substances / emergency / sequence). */
  competenceOff: string[];
  substancesOff: string[];
  substancesAdd: string[];
  emergencyOff: string[];
  sequenceOff: number[];
  /** Site fields made required by current answers. */
  requireFields: string[];
  /** Visible questions given current answers (follow-ups included when revealed). */
  visible: TriggerQuestion[];
  answered: number;
};

/** Hazard ids a question (incl. follow-ups) can control — used to clear manual
 *  overrides when its answer changes. */
export function controlledHazards(q: TriggerQuestion): string[] {
  const ids: string[] = [];
  for (const e of [...Object.values(q.effects || {}), ...(q.notSure ? [q.notSure] : [])]) {
    ids.push(...(e.hazardsOn || []), ...(e.hazardsOff || []));
  }
  for (const f of q.followUps?.questions || []) ids.push(...controlledHazards(f));
  return ids;
}

export function evaluateTriggers(questions: TriggerQuestion[], answers: Record<string, string>): TriggerOutcome {
  const out: TriggerOutcome = {
    off: new Set(), on: new Set(), permitsAdd: [], permitsRemove: [], scopeLines: [], checks: [],
    competenceOff: [], substancesOff: [], substancesAdd: [], emergencyOff: [], sequenceOff: [], requireFields: [],
    visible: [], answered: 0,
  };

  function walk(qs: TriggerQuestion[]) {
    for (const q of qs) {
      out.visible.push(q);
      const answer = (answers[q.key] || "").trim();
      if (answer) out.answered++;
      if (q.kind === "text" && q.requiredCheck) out.checks.push({ label: q.requiredCheck, ok: !!answer });
      const effect = !answer
        ? undefined
        : answer === NOT_SURE
          ? q.notSure
          : q.kind === "text"
            ? q.effects?.["*"]
            : q.effects?.[answer];
      if (effect) {
        for (const id of effect.hazardsOff || []) out.off.add(id);
        for (const id of effect.hazardsOn || []) out.on.add(id);
        out.permitsAdd.push(...(effect.permitsAdd || []));
        out.permitsRemove.push(...(effect.permitsRemove || []));
        if (effect.scopeLine) out.scopeLines.push(effect.scopeLine.replace("{answer}", answer));
        if (effect.failCheck) out.checks.push({ label: effect.failCheck, ok: false });
        out.competenceOff.push(...(effect.competenceOff || []));
        out.substancesOff.push(...(effect.substancesOff || []));
        if (effect.substancesAdd) out.substancesAdd.push(effect.substancesAdd.replace("{answer}", answer));
        out.emergencyOff.push(...(effect.emergencyOff || []));
        out.sequenceOff.push(...(effect.sequenceOff || []));
        out.requireFields.push(...(effect.requireFields || []));
      }
      if (q.followUps && answer === q.followUps.when) walk(q.followUps.questions);
    }
  }
  walk(questions);
  return out;
}

/** Report with the trigger-question content effects applied — competence,
 *  substances, emergency scenarios and sequence steps follow the answers, so
 *  the document never references work the user said isn't happening. Hazards
 *  are untouched (the enabled set handles those). */
export function applyTriggerContent(report: RamsReport, o: TriggerOutcome): RamsReport {
  const hit = (text: string, matchers: string[]) => matchers.some((m) => text.toLowerCase().includes(m.toLowerCase()));
  let emergencyRescue = report.emergencyRescue;
  if (o.emergencyOff.length) {
    emergencyRescue = splitEmergency(report.emergencyRescue)
      .filter((s) => !(s.heading && hit(s.heading, o.emergencyOff)))
      .map((s) => (s.heading ? `${s.heading}: ${s.body}` : s.body))
      .join(" ");
  }
  return {
    ...report,
    competence: report.competence.filter((c) => !hit(c.requirement, o.competenceOff)),
    substances: [...report.substances.filter((s) => !hit(s, o.substancesOff)), ...o.substancesAdd],
    sequenceOfWorks: report.sequenceOfWorks.filter((s) => !o.sequenceOff.includes(s.order)),
    emergencyRescue,
  };
}
